/**
 * Affiliate Bonus Item Handler
 * 
 * Adds a free bonus venison chips item to cart when user adds any product
 * on an affiliate page AND the cart total reaches $100+. The bonus item is 
 * marked with a hidden "_bonus_item" property to exempt it from the minimum 
 * quantity of 2 rule.
 */

(function() {
  'use strict';

  // Configuration
  const BONUS_PRODUCT_HANDLE = 'wild-axis-deer-venison-chips';
  const BONUS_VARIANT_ID = 44225991114867; // Hardcoded variant ID for Wild Axis Deer Venison Chips (1oz Pack)
  const BONUS_DISCOUNT_CODES = ['LUKE']; // Discount codes that trigger the bonus (uppercase)
  const STORAGE_KEY = 'affiliate_bonus_added';
  const MINIMUM_CART_TOTAL_CENTS = 10000; // $100.00 in cents - minimum cart total required for bonus
  
  // Detect local development environment - disable bonus logic to avoid rate limiting
  const IS_LOCAL_DEV = window.location.hostname === '127.0.0.1' || 
                       window.location.hostname === 'localhost' ||
                       window.location.port === '9292';
  
  // Lock to prevent concurrent add attempts
  let isAddingBonus = false;
  
  // Cache cart data to reduce API calls
  let cachedCart = null;
  let cacheTimestamp = 0;
  const CACHE_TTL = 3000; // Cache cart for 3 seconds

  /**
   * Fetch the current cart with retry logic and caching
   */
  async function fetchCart(retries = 2, delay = 1000, skipCache = false) {
    // Return cached cart if still valid
    if (!skipCache && cachedCart && (Date.now() - cacheTimestamp) < CACHE_TTL) {
      console.log('[AffiliateBonusItem] Using cached cart data');
      return cachedCart;
    }
    
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const response = await fetch('/cart.js');
        
        // Check for rate limiting or error responses
        if (response.status === 429) {
          console.warn('[AffiliateBonusItem] Rate limited, attempt', attempt, 'of', retries);
          if (attempt < retries) {
            await new Promise(resolve => setTimeout(resolve, delay * attempt * 2)); // Longer delay for rate limits
            continue;
          }
          // Return cached data if we have it, even if stale
          if (cachedCart) {
            console.log('[AffiliateBonusItem] Rate limited, using stale cache');
            return cachedCart;
          }
          return null;
        }
        
        if (!response.ok) {
          console.warn('[AffiliateBonusItem] Cart fetch failed with status:', response.status);
          if (attempt < retries) {
            await new Promise(resolve => setTimeout(resolve, delay * attempt));
            continue;
          }
          return cachedCart || null;
        }
        
        const text = await response.text();
        // Check if response is actually JSON (not HTML error page)
        if (text.startsWith('<!') || text.startsWith('<html')) {
          console.warn('[AffiliateBonusItem] Received HTML instead of JSON, attempt', attempt);
          if (attempt < retries) {
            await new Promise(resolve => setTimeout(resolve, delay * attempt));
            continue;
          }
          return cachedCart || null;
        }
        
        // Parse and cache the result
        cachedCart = JSON.parse(text);
        cacheTimestamp = Date.now();
        return cachedCart;
      } catch (e) {
        console.error('[AffiliateBonusItem] Error fetching cart (attempt ' + attempt + '):', e);
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, delay * attempt));
          continue;
        }
        return cachedCart || null;
      }
    }
    return cachedCart || null;
  }
  
  /**
   * Invalidate the cart cache (call after modifying cart)
   */
  function invalidateCartCache() {
    cachedCart = null;
    cacheTimestamp = 0;
  }

  /**
   * Check if a bonus item already exists in the cart
   */
  async function checkCartForBonusItem() {
    try {
      const cart = await fetchCart();
      if (!cart) return false;
      
      return cart.items.some(item => {
        if (item.properties && item.properties._bonus_item) {
          return true;
        }
        return false;
      });
    } catch (e) {
      console.error('[AffiliateBonusItem] Error checking cart for bonus:', e);
      return false;
    }
  }

  /**
   * Calculate the cart total excluding bonus items
   * @returns {number} Cart total in cents, excluding bonus items
   */
  async function getCartTotalExcludingBonus() {
    try {
      const cart = await fetchCart();
      if (!cart || !cart.items) return 0;
      
      // Sum up prices of non-bonus items
      let total = 0;
      for (const item of cart.items) {
        // Skip bonus items
        if (item.properties && item.properties._bonus_item) {
          continue;
        }
        total += item.final_line_price; // This is in cents
      }
      
      console.log('[AffiliateBonusItem] Cart total (excluding bonus):', total, 'cents ($' + (total / 100).toFixed(2) + ')');
      return total;
    } catch (e) {
      console.error('[AffiliateBonusItem] Error calculating cart total:', e);
      return 0;
    }
  }

  /**
   * Check if cart total meets the minimum threshold for the bonus
   */
  async function cartMeetsMinimum() {
    const total = await getCartTotalExcludingBonus();
    const meetsMinimum = total >= MINIMUM_CART_TOTAL_CENTS;
    console.log('[AffiliateBonusItem] Minimum threshold check:', 
      'Cart: $' + (total / 100).toFixed(2), 
      'Required: $' + (MINIMUM_CART_TOTAL_CENTS / 100).toFixed(2),
      'Meets minimum:', meetsMinimum
    );
    return meetsMinimum;
  }

  /**
   * Check if bonus was already added this session
   */
  function wasBonusAlreadyAdded() {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
        if (data.added === true && data.timestamp > oneDayAgo) {
          return true;
        }
      }
    } catch (e) {
      console.error('[AffiliateBonusItem] Error reading session storage:', e);
    }
    return false;
  }

  /**
   * Mark the bonus as added
   */
  function markBonusAsAdded() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        added: true,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.error('[AffiliateBonusItem] Error writing session storage:', e);
    }
  }

  /**
   * Add the bonus item to cart with retry logic
   */
  async function addBonusItemToCart(retries = 2) {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        console.log('[AffiliateBonusItem] Adding bonus venison chips, variant:', BONUS_VARIANT_ID, '(attempt ' + attempt + ')');
        
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            id: BONUS_VARIANT_ID,
            quantity: 1,
            properties: {
              '_bonus_item': 'true',
              '_discount_bonus': 'Free with qualifying purchase',
              '_min_quantity': '1',
              '_skip_min_quantity': 'true'
            }
          })
        });

        // Handle rate limiting
        if (response.status === 429) {
          console.warn('[AffiliateBonusItem] Rate limited on add, attempt', attempt, 'of', retries);
          if (attempt < retries) {
            await new Promise(resolve => setTimeout(resolve, 2000 * attempt));
            continue;
          }
          return false;
        }

        if (!response.ok) {
          const errorText = await response.text();
          console.warn('[AffiliateBonusItem] Add failed with status:', response.status, errorText);
          if (attempt < retries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
            continue;
          }
          return false;
        }

        const text = await response.text();
        // Check if response is actually JSON
        if (text.startsWith('<!') || text.startsWith('<html')) {
          console.warn('[AffiliateBonusItem] Received HTML instead of JSON on add, attempt', attempt);
          if (attempt < retries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
            continue;
          }
          return false;
        }

        const result = JSON.parse(text);
        console.log('[AffiliateBonusItem] Bonus item added successfully:', result);
        
        // Invalidate cache since cart changed
        invalidateCartCache();
        
        // Mark as added to prevent duplicate attempts
        markBonusAsAdded();
        
        // Trigger cart UI refresh (without additional API calls - let UpCart handle its own refresh)
        setTimeout(() => {
          try {
            // Refresh cart drawer if available
            if (typeof window.refreshCart === 'function') {
              window.refreshCart();
            }
            // Dispatch event for other cart listeners
            document.dispatchEvent(new CustomEvent('cart:updated'));
          } catch (e) {
            console.warn('[AffiliateBonusItem] Error triggering cart refresh:', e);
          }
        }, 100);
        
        return true;
      } catch (e) {
        console.error('[AffiliateBonusItem] Error adding bonus item (attempt ' + attempt + '):', e);
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, 500 * attempt));
          continue;
        }
        return false;
      }
    }
    return false;
  }

  /**
   * Check if we should add the bonus item
   * Uses "one bonus per cart" logic and requires cart to meet $100 minimum
   */
  async function shouldAddBonus() {
    // Check if already in process of adding
    if (isAddingBonus) {
      console.log('[AffiliateBonusItem] Already processing bonus addition, skipping');
      return false;
    }
    
    // Check if cart already has bonus item
    const hasBonusItem = await checkCartForBonusItem();
    if (hasBonusItem) {
      console.log('[AffiliateBonusItem] Cart already contains a bonus item');
      return false;
    }

    // Check if cart total meets the minimum threshold ($100)
    const meetsMinimum = await cartMeetsMinimum();
    if (!meetsMinimum) {
      console.log('[AffiliateBonusItem] Cart does not meet minimum threshold for bonus');
      return false;
    }

    console.log('[AffiliateBonusItem] No bonus item in cart and meets minimum, will add one');
    return true;
  }

  /**
   * Handle add to cart - intercept and add bonus item
   */
  async function handleAddToCart() {
    // Prevent concurrent executions
    if (isAddingBonus) {
      console.log('[AffiliateBonusItem] Already processing, skipping duplicate trigger');
      return;
    }
    
    console.log('[AffiliateBonusItem] Add to cart detected on affiliate page');
    
    // Small delay to let the main item be added first
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Check if we should add bonus
    const shouldAdd = await shouldAddBonus();
    if (!shouldAdd) {
      return;
    }

    // Set lock and add the bonus item
    isAddingBonus = true;
    try {
      await addBonusItemToCart();
    } finally {
      // Release lock after a delay to prevent rapid re-triggering
      setTimeout(() => {
        isAddingBonus = false;
      }, 2000);
    }
  }

  /**
   * Intercept fetch calls to /cart/add.js and /cart/change.js
   */
  function interceptFetch() {
    const originalFetch = window.fetch;
    
    window.fetch = async function(...args) {
      const result = await originalFetch.apply(this, args);
      
      // Check if this was a cart modification request
      const url = args[0];
      if (typeof url === 'string' && (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update'))) {
        // Don't intercept our own bonus item additions
        const body = args[1]?.body;
        if (body && typeof body === 'string' && body.includes('_bonus_item')) {
          return result;
        }
        
        // Invalidate cache since cart changed
        invalidateCartCache();
        
        // Trigger bonus item check (handles both add and quantity changes)
        handleAddToCart();
      }
      
      return result;
    };
    
    console.log('[AffiliateBonusItem] Fetch interceptor installed (add/change/update)');
  }

  /**
   * Listen for form submissions (traditional add to cart)
   */
  function interceptForms() {
    document.addEventListener('submit', function(e) {
      const form = e.target;
      if (form.action && form.action.includes('/cart/add')) {
        console.log('[AffiliateBonusItem] Form submission to cart detected');
        // For form submissions, we need to add bonus after page might reload
        // Store a flag and check on next page load
        sessionStorage.setItem('affiliate_bonus_pending', 'true');
      }
    });
  }

  /**
   * Check for pending bonus addition (from form submission)
   */
  async function checkPendingBonus() {
    const pending = sessionStorage.getItem('affiliate_bonus_pending');
    if (pending === 'true') {
      sessionStorage.removeItem('affiliate_bonus_pending');
      console.log('[AffiliateBonusItem] Processing pending bonus from form submission');
      
      const shouldAdd = await shouldAddBonus();
      if (shouldAdd) {
        await addBonusItemToCart();
      }
    }
  }

  /**
   * Listen for click events on add to cart buttons (backup method)
   */
  function interceptClicks() {
    document.addEventListener('click', async function(e) {
      const button = e.target.closest('button[type="submit"], .add-to-cart, [data-js-product-add-to-cart], .quick-add-to-cart button');
      
      if (button) {
        const form = button.closest('form');
        if (form && form.action && (form.action.includes('/cart/add') || form.action.includes('/cart'))) {
          console.log('[AffiliateBonusItem] Add to cart button clicked');
          
          // Wait for the add to cart to complete
          setTimeout(async () => {
            const shouldAdd = await shouldAddBonus();
            if (shouldAdd) {
              await addBonusItemToCart();
            }
          }, 1000);
        }
      }
    });
    
    console.log('[AffiliateBonusItem] Click interceptor installed');
  }

  /**
   * Initialize the script
   */
  function init() {
    // Skip in local development to avoid rate limiting issues
    if (IS_LOCAL_DEV) {
      console.log('[AffiliateBonusItem] Local dev detected - bonus script disabled to prevent rate limiting');
      console.log('[AffiliateBonusItem] To test, use: window.AffiliateBonusItem.forceEnable()');
      return;
    }
    
    const scriptElement = document.querySelector('script[data-affiliate-bonus]');
    if (!scriptElement) {
      console.log('[AffiliateBonusItem] Script element not found, not on affiliate page');
      return;
    }

    console.log('[AffiliateBonusItem] Initializing on affiliate page...');
    console.log('[AffiliateBonusItem] Bonus variant ID:', BONUS_VARIANT_ID);

    // Install interceptors
    interceptFetch();
    interceptClicks();
    interceptForms();
    
    // Check for pending bonus from previous form submission
    checkPendingBonus();
    
    console.log('[AffiliateBonusItem] Ready - bonus will be added when cart reaches $' + (MINIMUM_CART_TOTAL_CENTS / 100).toFixed(2));
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for manual triggering/debugging
  window.AffiliateBonusItem = {
    addBonusItem: addBonusItemToCart,
    checkCartForBonusItem: checkCartForBonusItem,
    shouldAddBonus: shouldAddBonus,
    cartMeetsMinimum: cartMeetsMinimum,
    getCartTotal: getCartTotalExcludingBonus,
    MINIMUM_THRESHOLD: MINIMUM_CART_TOTAL_CENTS,
    isLocalDev: IS_LOCAL_DEV,
    clearSession: function() {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem('affiliate_bonus_pending');
      invalidateCartCache();
      console.log('[AffiliateBonusItem] Session cleared - bonus will be added on next add to cart');
    },
    // Force enable in local dev (use with caution - may cause rate limiting)
    forceEnable: function() {
      console.log('[AffiliateBonusItem] Force enabling in local dev...');
      interceptFetch();
      interceptClicks();
      interceptForms();
      checkPendingBonus();
      console.log('[AffiliateBonusItem] Enabled - bonus will be added when cart reaches $' + (MINIMUM_CART_TOTAL_CENTS / 100).toFixed(2));
    },
    // Manual trigger for testing
    triggerBonus: async function() {
      const shouldAdd = await shouldAddBonus();
      if (shouldAdd) {
        await addBonusItemToCart();
      } else {
        console.log('[AffiliateBonusItem] Bonus already added or in cart, or cart below $' + (MINIMUM_CART_TOTAL_CENTS / 100).toFixed(2));
      }
    }
  };

})();
